import { Box, Text, Flex, UnorderedList, ListItem } from "@chakra-ui/react";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import PageHeader from "components/layout/PageHeader";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { Link, Outlet, useLocation } from "react-router-dom";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";

const SettingsMenu = ({
  items,
  pathname,
}: {
  items: {
    title: string;
    path: string;
  }[];
  pathname: string;
}) => {
  return (
    <UnorderedList listStyleType="none" p="0px" m="10px 0px 30px 0px" w="100%">
      {items.map(item => (
        <ListItem
          key={item.path}
          color="black"
          fontWeight={`/settings/${item.path}` === pathname ? "700" : "500"}
          fontSize="15px"
          lineHeight="21px"
          py="10px"
        >
          <Link to={item.path}>{item.title}</Link>
        </ListItem>
      ))}
    </UnorderedList>
  );
};

const SettingsMenuHeader = ({ title }: { title: string }) => {
  return (
    <Text color="gray.200" fontSize="15px" lineHeight="21px" fontWeight="500">
      {title}
    </Text>
  );
};

function Settings() {
  const { pathname } = useLocation();
  const { user } = useTenant();

  const generalItems =
    user.type === "lender"
      ? [
          {
            title: "User Roles & User Permissions",
            path: "permissions",
          },
          {
            title: "Checklist Items",
            path: "checklist",
          },
          {
            title: "Loan Fields",
            path: "fields",
          },
          {
            title: "Integrations",
            path: "integrations",
          },
          {
            title: "Branding",
            path: "branding",
          },
        ]
      : [];

  const filterItemsByPermissions = (items: any) => {
    let filteredItems = [...items];

    if (
      !hasPermission(user, permissionConstants.viewUserRolesPermissions) &&
      isPermissionEnabled(user)
    ) {
      filteredItems = filteredItems.filter(
        (item: any) => item.title !== "User Roles & User Permissions"
      );
    }

    if (!hasPermission(user, permissionConstants.viewLoanFields) && isPermissionEnabled(user)) {
      filteredItems = filteredItems.filter((item: any) => item.title !== "Loan Fields");
    }

    const canEditChecklist = hasPermission(user, permissionConstants.editChecklist);
    const canAddChecklist = hasPermission(user, permissionConstants.addChecklist);
    const canViewChecklist = canAddChecklist || canEditChecklist || !isPermissionEnabled(user);

    if (!canViewChecklist && isPermissionEnabled(user)) {
      filteredItems = filteredItems.filter((item: any) => item.title !== "Checklist Items");
    }

    return filteredItems;
  };

  const filteredItems = filterItemsByPermissions(generalItems);

  return (
    <>
      <PageHeader title="Settings" />
      <WidgetBox>
        <Flex>
          <Box borderRightWidth="1px" borderRightColor="gray.50" p={4} minW="220px">
            <SettingsMenuHeader title="Personal" />
            <SettingsMenu
              pathname={pathname}
              items={
                user.type === "lender"
                  ? [
                      {
                        title: "Account Info",
                        path: "account",
                      },
                    ]
                  : [
                      {
                        title: "Account Info",
                        path: "account",
                      },
                    ]
              }
            />
            {filteredItems.length > 0 && (
              <>
                <SettingsMenuHeader title="General" />
                <SettingsMenu pathname={pathname} items={filteredItems} />
              </>
            )}
          </Box>
          <Box p={4} pl={8}>
            <Outlet />
          </Box>
        </Flex>
      </WidgetBox>
    </>
  );
}

export default Settings;
